import type { MessageKey } from '../i18n';

// Help (#92, docs/help.md D290): what /help lists, by topic. The words are
// in the language catalogs (i18n/<lang>/help.ts); this only orders them. Each
// entry's id is its link: /help#add-device.

export type HelpTopic =
  | 'start'
  | 'chats'
  | 'calls'
  | 'family'
  | 'devices'
  | 'privacy'
  | 'notifications'
  | 'account'
  | 'about';

export interface HelpEntry {
  id: string;
  topic: HelpTopic;
  kind: 'guide' | 'question';
  title: MessageKey;
  /** A guide's steps (one per line) or a question's answer (paragraphs). */
  body: MessageKey;
}

export const HELP_TOPICS: HelpTopic[] = [
  'start',
  'chats',
  'calls',
  'family',
  'devices',
  'notifications',
  'privacy',
  'account',
  'about',
];

const question = (id: string, topic: HelpTopic, key: string): HelpEntry => ({
  id,
  topic,
  kind: 'question',
  title: `help.q.${key}.q` as MessageKey,
  body: `help.q.${key}.a` as MessageKey,
});

const guide = (id: string, topic: HelpTopic, key: string): HelpEntry => ({
  id,
  topic,
  kind: 'guide',
  title: `help.g.${key}.title` as MessageKey,
  body: `help.g.${key}.steps` as MessageKey,
});

export const HELP_ENTRIES: HelpEntry[] = [
  guide('join', 'start', 'join'),
  guide('install', 'notifications', 'install'),
  guide('notifications', 'notifications', 'notifications'),
  guide('add-someone', 'family', 'inviteNew'),
  guide('family-link', 'family', 'familyLink'),
  guide('switch-family', 'family', 'switchFamily'),
  guide('relation-names', 'family', 'relationName'),
  guide('family-apps', 'family', 'familyApps'),
  guide('add-device', 'devices', 'addDevice'),
  guide('computer-sign-in', 'devices', 'computerSignIn'),
  guide('sign-out-device', 'devices', 'signOutDevice'),
  guide('new-chat', 'chats', 'newChat'),
  guide('send-photos', 'chats', 'sendMedia'),
  guide('poll', 'chats', 'poll'),
  guide('gathering', 'chats', 'gathering'),
  guide('greeting', 'chats', 'greeting'),
  guide('send-later', 'chats', 'sendLater'),
  guide('message-actions', 'chats', 'messageActions'),
  guide('chat-options', 'chats', 'chatOptions'),
  guide('search', 'chats', 'search'),
  guide('call', 'calls', 'call'),
  guide('block-report', 'privacy', 'blockReport'),
  guide('profile', 'account', 'profile'),
  guide('leave-family', 'account', 'leaveFamily'),
  question('what-is-twinden', 'start', 'whatIs'),
  question('invite-only', 'start', 'inviteOnly'),
  question('cost', 'start', 'free'),
  question('several-families', 'family', 'severalFamilies'),
  question('can-twinden-read', 'privacy', 'canRead'),
  question('calls-private', 'privacy', 'calls'),
  question('security-codes', 'privacy', 'securityCodes'),
  question('block', 'privacy', 'block'),
  question('disappearing', 'chats', 'disappearing'),
  question('passkey', 'devices', 'passkey'),
  question('lost-phone', 'devices', 'lostPhone'),
  question('app-lock', 'devices', 'appLock'),
  question('iphone-notifications', 'notifications', 'iphoneNotifications'),
  question('no-notifications', 'notifications', 'noNotifications'),
  question('delete-account', 'account', 'deleteAccount'),
  question('external-passkeys', 'devices', 'externalPasskeys'),
  question('why-twinden', 'about', 'why'),
  question('name', 'about', 'name'),
  question('different', 'about', 'different'),
  question('why-encrypted', 'privacy', 'whyEncrypted'),
  question('encryption-strength', 'privacy', 'strength'),
  question('what-is-stored', 'privacy', 'stored'),
  question('know-you', 'privacy', 'knowYou'),
  question('where-stored', 'privacy', 'where'),
  question('why-singapore', 'privacy', 'whySingapore'),
  question('indian-cloud', 'privacy', 'indianCloud'),
  question('sell-data', 'privacy', 'sellData'),
  question('vision', 'about', 'vision'),
  question('money', 'about', 'money'),
  question('open-source', 'about', 'openSource'),
  question('ask-for-features', 'about', 'features'),
  question('slow', 'about', 'slow'),
];

/** Lowercase, without accents, so "cafe" finds "Café"; other scripts pass through. */
function fold(text: string): string {
  return text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/** Entries whose words (in the shown language) contain every word searched for. */
export function searchHelp(
  entries: HelpEntry[],
  query: string,
  words: (entry: HelpEntry) => string
): HelpEntry[] {
  const terms = fold(query).split(/\s+/).filter(Boolean);
  if (terms.length === 0) return entries;
  return entries.filter((entry) => {
    const haystack = fold(words(entry));
    return terms.every((term) => haystack.includes(term));
  });
}
